import React, { useCallback, useRef, useEffect, useState } from 'react';

import { useParams, useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { Divider } from '@material-ui/core';

import { HiOutlinePencil } from 'react-icons/hi';
import { MdWork, MdEmail, MdLock } from 'react-icons/md';
import { IoIosArrowBack } from 'react-icons/io';
import * as S from './styles';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

import api from '../../services/api';

import getValidationErrors from '../../utils/getValidationErrors';

import Input from '../../newComponents/UnformInput';
import Select from '../../newComponents/UnformSelect';
import Button from '../../newComponents/Button';
import Toggle from '../../newComponents/Toggle';

interface CreateUserRequest {
  nome: string;
  cargo: string;
  email: string;
  senha: string;
  tipo_acesso: string;
}

interface Option {
  id: string;
  value: string;
  label: string;
}

const CreateFuncionarioV2: React.FC = () => {
  const { id } = useParams<any>();

  const history = useHistory();
  const { user, rotinas, findRotinas } = useAuth();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOption, setSelectedOption] = useState({
    id: '',
    value: '',
    label: '',
  });
  const [acessoPdv, setAcessoPdv] = useState(true);

  const hasPermission = useCallback(
    (): boolean => findRotinas('funcionarios')?.alterar || false,
    [findRotinas],
  );

  const handleCreateUser = useCallback(
    async (data: CreateUserRequest) => {
      try {
        setIsLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          nome: Yup.string().required(
            'Infome o nome do funcionário para continuar',
          ),
          cargo: Yup.string().required(
            'Informe o cargo do funcionário para continuar',
          ),
          email: Yup.string()
            .required('Informe o email do funcionário para continuar.')
            .email('Digite um e-mail válido'),
          senha: Yup.string()
            .required('Informe a senha para continuar')
            .min(4, 'A senha precisar ter no mínimo 4 caracteres'),
          tipo_acesso: Yup.string().required(
            'Informe o tipo de acesso para continuar.',
          ),
        });

        await schema.validate(data, { abortEarly: false });

        const { nome, cargo, email, senha, tipo_acesso } = data;

        api
          .post('/usuario', {
            email,
            senha,
            cargo,
            nome,
            tipo_acesso,
            acesso_pdv: acessoPdv,
            empresa_id: user.empresa_id,
          })
          .then(() => {
            addToast({
              type: 'success',
              title: 'Funcionário Criado',
            });
            history.push('/configuracoes/4');
            setIsLoading(false);
          })
          .catch((err) => {
            if (
              err.response.status === 409 &&
              err?.response.data.message ===
                'Já existe um usuário cadastradado com esse login'
            ) {
              formRef.current?.setErrors({
                email: 'Email já possui cadastrado',
              });
            } else if (err.response.status === 403) {
              addToast({
                type: 'error',
                title: `${err.response.data.message}`,
              });
            } else {
              addToast({
                type: 'error',
                title: 'Erro no servidor',
              });
            }
            setIsLoading(false);
          });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          console.log(errors);
          formRef.current?.setErrors(errors);
        } else {
          addToast({
            type: 'error',
            title: 'Erro durante a criação',
          });
        }
        setIsLoading(false);
      }
    },
    [user.empresa_id, addToast, history, acessoPdv],
  );

  useEffect(() => {
    if (rotinas && !hasPermission()) {
      addToast({
        type: 'error',
        title: 'Não autorizado.',
        description: 'Você não tem permissão.',
      });
      history.push('/configuracoes/0');
    }

    async function loadAcessos(): Promise<void> {
      const response = await api.get('/acesso');

      const optionItems: Option[] = [];

      response.data.map((acesso: any): void => {
        const optionFormat = {
          id: acesso.id,
          label: acesso.nome,
          value: acesso.role,
        };

        optionItems.push(optionFormat);
      });
      setOptions(optionItems);
      setIsLoading(false);
    }

    loadAcessos();
  }, [addToast, hasPermission, history, id, rotinas]);

  return (
    <S.Container>
      <header>
        <button
          className="previous_page"
          type="button"
          onClick={() => history.push('/configuracoes/4')}
        >
          <IoIosArrowBack size={20} />
          Voltar
        </button>
        <div>
          <h1>Adicionar Funcionários</h1>
          <p>Preencha os dados abaixo para adicionar um novo funcionário.</p>
        </div>
      </header>

      <Divider />

      <S.CustomForm ref={formRef} onSubmit={handleCreateUser}>
        <div className="bloco-input multi">
          <Input
            name="nome"
            icon={HiOutlinePencil}
            label="Nome:"
            placeholder="Ex.: John Doe"
            disabled={isLoading}
          />
          <Input
            name="cargo"
            icon={MdWork}
            label="Cargo:"
            placeholder="Ex.: Gerente"
            disabled={isLoading}
          />
        </div>
        <div className="bloco-input multi">
          <Input
            name="email"
            icon={MdEmail}
            label="E-mail:"
            placeholder="Ex.: johndoe@example.com"
            disabled={isLoading}
          />
          <Input
            name="senha"
            icon={MdLock}
            type="password"
            label="Senha:"
            placeholder="Ex.: ********"
            disabled={isLoading}
          />
        </div>
        <div className="bloco-input">
          <label htmlFor="tipo_acesso">Tipo de Acesso:</label>
          <Select
            name="tipo_acesso"
            placeholder="Ex.: Administrador"
            options={options}
            value={selectedOption}
            onChange={(value: any) => setSelectedOption(value)}
            disabled={isLoading}
          />
        </div>
        <div className="bloco-input">
          <label htmlFor="acesso_pdv">Pode acessar o PDV</label>
          <Toggle
            firstText="Não"
            secondText="Sim"
            checked={acessoPdv}
            onToggle={setAcessoPdv}
          />
        </div>

        <div className="buttons">
          <Button full type="submit" disabled={isLoading}>
            Salvar
          </Button>
        </div>
      </S.CustomForm>
    </S.Container>
  );
};

export default CreateFuncionarioV2;
